<template>
    <form class="pform" @submit.prevent="onSubmit">
        <div class="pform__grid">
            <AdmField
                class="pform__span-2"
                v-model="form.name"
                label="Nombre"
                required
                maxlength="120"
            />

            <AdmField
                class="pform__span-2"
                v-model="form.description"
                label="Descripción"
                type="textarea"
                :rows="2"
                maxlength="500"
            />

            <AdmField
                class="pform__span-2"
                v-model="form.materialId"
                label="Material"
                type="select"
                required
                :hint="materials.length ? '' : 'No hay materiales activos: crea uno desde Materiales.'"
            >
                <option disabled value="">Seleccione un material</option>
                <option v-for="m in materials" :key="m.id" :value="m.id">
                    #{{ m.id }} — {{ m.name }}
                </option>
            </AdmField>

            <AdmField
                class="pform__span-2"
                v-model="form.categoryId"
                label="Categoría del catálogo"
                type="select"
                :hint="categories.length ? '' : 'No hay categorías: créalas desde Categorías para que el catálogo público se pueda filtrar.'"
            >
                <option value="">Sin categoría</option>
                <option v-for="c in categories" :key="c.id" :value="c.id">
                    {{ c.name }}{{ c.isActive === false ? ' (inactiva)' : '' }}
                </option>
            </AdmField>

            <label class="pform__span-2 pform__featured">
                <input v-model="form.isFeatured" type="checkbox" />
                <span>
                    Mostrar en el catálogo principal (temporada)
                    <small class="adm-muted">
                        Los destacados salen en el carrusel de <code>/print3d</code>; el resto
                        sólo en el catálogo completo.
                    </small>
                </span>
            </label>

            <fieldset class="pform__span-2 pform__images">
                <legend>Imágenes del producto</legend>

                <div
                    class="pform__dropzone"
                    :class="{ 'is-dragover': isDragOver }"
                    @click="triggerFileInput"
                    @dragover.prevent="isDragOver = true"
                    @dragleave.prevent="isDragOver = false"
                    @drop.prevent="onDrop"
                >
                    <input
                        ref="fileInput"
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        multiple
                        @change="onFileChange"
                        hidden
                    />
                    <p>
                        Arrastra una o varias imágenes, o haz clic para seleccionarlas
                        <small class="adm-muted">PNG, JPG, WEBP o SVG — máx 5 MB c/u, hasta {{ MAX_IMAGES }} por producto</small>
                    </p>
                </div>

                <!-- La galería es una lista ordenada: la primera posición es la
                     que el catálogo público usa como portada. -->
                <ul v-if="gallery.length" class="pform__gallery">
                    <li v-for="(item, index) in gallery" :key="item.key" class="pform__thumb">
                        <AdmBadge v-if="index === 0" tone="info" label="Portada" class="pform__thumb-badge" />
                        <img :src="displayUrl(item)" :alt="`${form.name || 'Producto'} ${index + 1}`" />
                        <small class="pform__caption" :class="{ 'is-pending': !!item.file }">
                            <template v-if="item.file">
                                {{ item.file.name }} ({{ humanSize(item.file.size) }}) — sin subir
                            </template>
                            <template v-else>{{ item.url }}</template>
                        </small>
                        <div class="pform__thumb-actions">
                            <AdmButton
                                variant="secondary"
                                size="sm"
                                :disabled="index === 0"
                                aria-label="Mover antes"
                                @click="move(index, -1)"
                            >←</AdmButton>
                            <AdmButton
                                variant="secondary"
                                size="sm"
                                :disabled="index === gallery.length - 1"
                                aria-label="Mover después"
                                @click="move(index, 1)"
                            >→</AdmButton>
                            <AdmButton variant="ghost" size="sm" @click="removeAt(index)">Quitar</AdmButton>
                        </div>
                    </li>
                </ul>
                <p v-else class="adm-muted">Sin imágenes. El catálogo mostrará el marco vacío.</p>

                <div class="pform__image-actions">
                    <AdmButton variant="secondary" size="sm" :icon="ImagePlus" @click="triggerFileInput">
                        Añadir imágenes
                    </AdmButton>
                    <AdmButton v-if="gallery.length" variant="ghost" size="sm" @click="clearImages">
                        Quitar todas
                    </AdmButton>
                </div>

                <details class="pform__manual-url">
                    <summary>O añadir una URL externa</summary>
                    <div class="pform__url-row">
                        <AdmField
                            v-model="externalUrl"
                            maxlength="255"
                            placeholder="https://… (URL absoluta) o /uploads/print3d/products/…"
                            hint="Se agrega al final de la galería."
                            @keydown.enter.prevent="addExternalUrl"
                        />
                        <AdmButton variant="secondary" @click="addExternalUrl">Añadir</AdmButton>
                    </div>
                </details>
            </fieldset>

            <AdmField v-model="form.sizeMmX" label="Largo X (mm)" type="number" :step="0.01" :min="0" required />
            <AdmField v-model="form.sizeMmY" label="Ancho Y (mm)" type="number" :step="0.01" :min="0" required />
            <AdmField v-model="form.sizeMmZ" label="Alto Z (mm)" type="number" :step="0.01" :min="0" required />
            <AdmField v-model="form.priceFrom" label="Costo desde (COP)" type="number" :step="0.01" :min="0" required />
            <AdmField v-model="form.currency" label="Moneda" maxlength="3" />
            <AdmField v-model="form.displayOrder" label="Orden de visualización" type="number" :step="1" :min="0" />

            <label v-if="editing" class="pform__check">
                <input v-model="form.isActive" type="checkbox" /> Activo
            </label>
        </div>

        <div class="pform__actions">
            <AdmButton variant="ghost" :disabled="uploading" @click="$emit('cancel')">Cancelar</AdmButton>
            <AdmButton type="submit" :loading="uploading">
                {{ uploading ? 'Subiendo imágenes…' : (editing ? 'Guardar cambios' : 'Crear producto') }}
            </AdmButton>
        </div>
    </form>
</template>

<script>
import { ImagePlus } from 'lucide-vue-next';
import { uploadProductImages } from '@/services/dbPrint3dAdmin.js';
import AdmField from '@/components/Admin/ui/AdmField.vue';
import AdmButton from '@/components/Admin/ui/AdmButton.vue';
import AdmBadge from '@/components/Admin/ui/AdmBadge.vue';
import { resolveImageUrl } from '@/services/print3dService.js';

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024;
// Tope alineado con el `maxCount` del endpoint de subida.
const MAX_IMAGES = 10;

// Clave estable de cada entrada de la galería: sin ella, reordenar reutilizaría
// los nodos de `v-for` y la miniatura mostraría la foto equivocada.
let galleryUid = 0;
const nextGalleryKey = () => `img-${++galleryUid}`;

export default {
    name: 'ProductoForm',
    components: { AdmField, AdmButton, AdmBadge },
    props: {
        value: { type: Object, default: () => ({}) },
        materials: { type: Array, default: () => [] },
        categories: { type: Array, default: () => [] },
        editing: { type: Boolean, default: false },
    },
    emits: ['submit', 'cancel'],
    data() {
        return {
            MAX_IMAGES,
            ImagePlus,
            form: this.buildInitialForm(this.value),
            // Galería ordenada. Cada entrada es una foto ya guardada
            // ({ url }) o un archivo pendiente de subir ({ file, previewUrl }).
            gallery: this.buildInitialGallery(this.value),
            externalUrl: '',
            isDragOver: false,
            uploading: false,
        };
    },
    watch: {
        value: {
            deep: true,
            handler(v) {
                this.form = this.buildInitialForm(v);
                this.revokePreviews();
                this.gallery = this.buildInitialGallery(v);
            },
        },
    },
    beforeUnmount() {
        this.revokePreviews();
    },
    methods: {
        buildInitialForm(v) {
            return {
                id: v.id ?? null,
                name: v.name ?? '',
                description: v.description ?? '',
                materialId: v.materialId ?? '',
                // '' (y no null) para que case con la opcion "Sin categoria".
                categoryId: v.categoryId ?? '',
                isFeatured: v.isFeatured ?? false,
                sizeMmX: v.sizeMmX ?? '',
                sizeMmY: v.sizeMmY ?? '',
                sizeMmZ: v.sizeMmZ ?? '',
                priceFrom: v.priceFrom ?? '',
                currency: v.currency ?? 'COP',
                displayOrder: v.displayOrder ?? 0,
                isActive: v.isActive !== undefined ? v.isActive : true,
            };
        },
        buildInitialGallery(v) {
            const urls = Array.isArray(v.images) && v.images.length
                ? v.images
                : (v.imageUrl ? [v.imageUrl] : []);
            return urls.filter(Boolean).map((url) => ({ key: nextGalleryKey(), url }));
        },
        displayUrl(item) {
            return item.file ? item.previewUrl : resolveImageUrl(item.url);
        },
        triggerFileInput() { this.$refs.fileInput.click(); },
        onFileChange(e) {
            this.acceptFiles(e.target.files);
            // Permite volver a elegir el mismo archivo tras quitarlo.
            e.target.value = '';
        },
        onDrop(e) {
            this.isDragOver = false;
            this.acceptFiles(e.dataTransfer?.files);
        },
        acceptFiles(fileList) {
            const files = Array.from(fileList || []);
            if (!files.length) return;
            const rechazados = [];
            for (const file of files) {
                if (this.gallery.length >= MAX_IMAGES) {
                    rechazados.push(`${file.name}: se alcanzó el máximo de ${MAX_IMAGES} imágenes.`);
                    continue;
                }
                if (!ACCEPTED_TYPES.includes(file.type)) {
                    rechazados.push(`${file.name}: tipo no permitido (${file.type || 'desconocido'}).`);
                    continue;
                }
                if (file.size > MAX_SIZE_BYTES) {
                    rechazados.push(`${file.name}: excede 5 MB.`);
                    continue;
                }
                this.gallery.push({
                    key: nextGalleryKey(),
                    file,
                    previewUrl: URL.createObjectURL(file),
                });
            }
            if (rechazados.length) {
                alert('No se añadieron estas imágenes:' + '\n\n' + rechazados.join('\n'));
            }
        },
        addExternalUrl() {
            const url = this.externalUrl.trim();
            if (!url) return;
            if (this.gallery.length >= MAX_IMAGES) {
                alert(`Máximo ${MAX_IMAGES} imágenes por producto.`);
                return;
            }
            if (this.gallery.some((item) => item.url === url)) {
                alert('Esa URL ya está en la galería.');
                return;
            }
            this.gallery.push({ key: nextGalleryKey(), url });
            this.externalUrl = '';
        },
        move(index, direction) {
            const target = index + direction;
            if (target < 0 || target >= this.gallery.length) return;
            const [item] = this.gallery.splice(index, 1);
            this.gallery.splice(target, 0, item);
        },
        removeAt(index) {
            const [item] = this.gallery.splice(index, 1);
            this.revokeItem(item);
        },
        clearImages() {
            this.revokePreviews();
            this.gallery = [];
        },
        revokeItem(item) {
            if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
        },
        revokePreviews() {
            this.gallery.forEach((item) => this.revokeItem(item));
        },
        humanSize(bytes) {
            if (!bytes) return '';
            if (bytes < 1024) return `${bytes} B`;
            if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
            return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
        },
        validate() {
            const required = ['name', 'materialId', 'sizeMmX', 'sizeMmY', 'sizeMmZ', 'priceFrom'];
            for (const k of required) {
                if (this.form[k] === '' || this.form[k] == null) {
                    alert(`Campo obligatorio: ${k}`);
                    return false;
                }
            }
            return true;
        },
        async onSubmit() {
            if (!this.validate()) return;

            // Las fotos nuevas se suben en una sola petición y el backend
            // devuelve las URLs en el mismo orden, así que basta con irlas
            // colocando en los huecos pendientes sin alterar la secuencia.
            const pendientes = this.gallery.filter((item) => item.file);
            let subidas = [];
            if (pendientes.length) {
                this.uploading = true;
                const { images, error } = await uploadProductImages(pendientes.map((i) => i.file));
                this.uploading = false;
                if (error) {
                    alert('Error al subir las imágenes: ' + error);
                    return;
                }
                subidas = images;
            }

            let cursor = 0;
            const urls = this.gallery.map((item) => (item.file ? subidas[cursor++] : item.url));
            const payload = {
                ...this.form,
                images: urls.filter(Boolean),
                // Espejo de la portada: el backend lo recalcula igual, pero así
                // el payload sigue siendo legible para quien lea la petición.
                imageUrl: urls[0] || '',
            };

            this.$emit('submit', payload);
            // El padre recarga el listado; los previews locales ya no se usan.
            this.revokePreviews();
            this.gallery = this.gallery.map((item, index) => (
                item.file ? { key: item.key, url: urls[index] } : item
            ));
        },
    },
};
</script>

<style scoped>
/* El marco (fondo, borde, título) lo pone ahora AdmCard desde la vista;
   aquí sólo queda la rejilla del formulario y la galería de imágenes. */
.pform {
    display: flex;
    flex-direction: column;
    gap: var(--adm-gap);
}

.pform__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--adm-gap);
}

.pform__span-2 { grid-column: span 2; }

.pform__check,
.pform__featured {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: var(--adm-font-sm);
    color: var(--color-text-strong);
}

.pform__featured { align-items: flex-start; }

.pform__featured span {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
}

.pform__featured code {
    background-color: var(--adm-surface-alt);
    padding: 0 0.2rem;
    border-radius: 3px;
}

.pform__images {
    border: 1px solid var(--adm-border);
    border-radius: var(--adm-radius-sm);
    padding: 0.75rem 1rem;
    margin: 0;
}

.pform__images legend {
    padding: 0 0.4rem;
    font-size: var(--adm-font-sm);
    font-weight: 600;
    color: var(--color-action-hover);
}

.pform__dropzone {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 120px;
    padding: var(--adm-gap);
    border: 2px dashed var(--adm-border-strong);
    border-radius: var(--adm-radius-sm);
    background-color: var(--adm-surface);
    color: var(--color-text);
    font-size: var(--adm-font-sm);
    text-align: center;
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease;
}

.pform__dropzone.is-dragover {
    border-color: var(--color-action);
    background-color: var(--adm-info-bg);
}

.pform__dropzone p {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.pform__gallery {
    list-style: none;
    margin: 0.75rem 0 0;
    padding: 0;
    display: grid;
    gap: 0.6rem;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
}

.pform__thumb {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    padding: 0.5rem;
    border: 1px solid var(--adm-border);
    border-radius: var(--adm-radius-sm);
    background-color: var(--adm-surface);
}

.pform__thumb img {
    width: 100%;
    height: 100px;
    object-fit: contain;
    border-radius: var(--adm-radius-sm);
    background-color: var(--adm-surface-alt);
}

.pform__thumb-badge {
    position: absolute;
    top: 0.35rem;
    left: 0.35rem;
    z-index: 1;
}

.pform__caption {
    font-size: 0.7rem;
    color: var(--color-text);
    word-break: break-all;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.pform__caption.is-pending { color: var(--adm-warn-fg); }

.pform__thumb-actions {
    display: flex;
    gap: 0.3rem;
    align-items: center;
}

.pform__image-actions {
    display: flex;
    gap: var(--adm-gap-sm);
    margin-top: var(--adm-gap-sm);
}

.pform__manual-url { margin-top: 0.6rem; }

.pform__manual-url summary {
    cursor: pointer;
    font-size: var(--adm-font-sm);
    color: var(--color-action);
}

.pform__url-row {
    display: flex;
    align-items: flex-start;
    gap: 0.4rem;
    margin-top: 0.4rem;
}

.pform__url-row > :first-child { flex: 1; }

.pform__actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--adm-gap-sm);
}

@media (max-width: 720px) {
    .pform__grid { grid-template-columns: 1fr; }
    .pform__span-2 { grid-column: span 1; }
}
</style>
